import React, { useEffect, useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	FormControl,
	FormControlLabel,
	InputAdornment,
	MenuItem,
	Paper,
	Radio,
	RadioGroup,
	Select,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import { calcularPrecioFinal } from 'features/salesOrder/components/SalesOrderServiceForm/helpers';
import { Destination, HotelOptions } from 'features/quotation/types';
import { LoadedQuotation, WizardSavedPassenger, getRoomBaseConfigFromQuotation, isFullQuotation } from '../../types';
import {
	AccommodationRoomInput,
	buildAccommodationServiceDraft,
	formatRoomConfigCaption,
	getPassengerIdsForRoomIndex,
	getRoomFareLabel,
} from '../../mappers/quotationServiceMappers';
import {
	hotelRoomsMatchRoomConfig,
	initRoomDraftsFromHotel,
	mapHotelProviderToAccommodationType,
} from '../../helpers/accommodationServicePayload';
import { isPnrFilled } from '../../helpers/flightServicePayload';
import { saveServiceToOrder } from '../../helpers/saveService';
import { WizardProviderAutocomplete } from '../WizardProviderAutocomplete';
import { tariffProviderNotAssignedId } from 'pages/SalesOrder/SalesOrderScreen/constants';

interface Props {
	saleOrderId: string;
	quotation: LoadedQuotation;
	savedPassengers: WizardSavedPassenger[];
	onComplete: () => void;
}

type RoomDraft = {
	roomFareIndex: number;
	cost: number;
	locator: string;
};

export const StepAccommodations = ({ saleOrderId, quotation, savedPassengers, onComplete }: Props): JSX.Element => {
	const destinations = useMemo(
		() => (isFullQuotation(quotation) ? quotation.destination?.filter((d) => d.hotelOptions?.length) || [] : []),
		[quotation],
	);
	const roomConfig = useMemo(() => getRoomBaseConfigFromQuotation(quotation), [quotation]);
	const markup = quotation.markup ?? 0;
	const multiRoom = roomConfig.length > 1;

	const [destIndex, setDestIndex] = useState(0);
	const [hotelIndex, setHotelIndex] = useState(0);
	const [roomDrafts, setRoomDrafts] = useState<RoomDraft[]>(() =>
		initRoomDraftsFromHotel(destinations[0]?.hotelOptions?.[0] ?? ({ rooms: [] } as HotelOptions), roomConfig.length),
	);
	const [splitByRoom, setSplitByRoom] = useState(false);
	const [serviceLocator, setServiceLocator] = useState('');
	const [locatorTouched, setLocatorTouched] = useState(false);
	const [tariffProviderId, setTariffProviderId] = useState(tariffProviderNotAssignedId);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const currentDest: Destination | undefined = destinations[destIndex];
	const hotels: HotelOptions[] = currentDest?.hotelOptions || [];
	const selectedHotel = hotels[hotelIndex];
	const hotelFares = selectedHotel?.rooms ?? [];

	useEffect(() => {
		if (!selectedHotel) {
			return;
		}
		setRoomDrafts(initRoomDraftsFromHotel(selectedHotel, roomConfig.length));
		setSplitByRoom(false);
		setServiceLocator('');
		setLocatorTouched(false);
		setTariffProviderId(tariffProviderNotAssignedId);
	}, [destIndex, hotelIndex, selectedHotel, roomConfig.length]);

	const totals = useMemo(() => {
		let totalCost = 0;
		let totalSale = 0;
		roomDrafts.forEach((draft) => {
			const cost = draft.cost || 0;
			if (cost <= 0) {
				return;
			}
			totalCost += cost;
			totalSale += parseFloat(calcularPrecioFinal(cost, markup));
		});
		return { totalCost, totalSale };
	}, [roomDrafts, markup]);

	const passengerLabelForRoom = (roomIndex: number): string => {
		const ids = getPassengerIdsForRoomIndex(roomIndex, quotation, savedPassengers);
		const names = ids
			.map((id) => {
				const p = savedPassengers.find((sp) => sp.keyId === id);
				return p ? `${p.name} ${p.lastName}`.trim() : '';
			})
			.filter(Boolean);
		return names.length > 0 ? names.join(', ') : 'Sin pasajeros asignados';
	};

	if (destinations.length === 0) {
		return (
			<Box>
				<Alert severity="info" sx={{ mb: 2 }}>
					No hay alojamientos en la cotización. Continuá con los demás servicios.
				</Alert>
				<Box display="flex" justifyContent="flex-end">
					<Button variant="contained" onClick={onComplete}>
						Continuar
					</Button>
				</Box>
			</Box>
		);
	}

	if (!currentDest || !selectedHotel) {
		return null;
	}

	const serviceLocatorError = locatorTouched && !splitByRoom && !isPnrFilled(serviceLocator);

	const updateRoomDraft = (roomIndex: number, patch: Partial<RoomDraft>) => {
		setRoomDrafts((prev) => prev.map((d, i) => (i === roomIndex ? { ...d, ...patch } : d)));
	};

	const handleFareChange = (roomIndex: number, fareIndex: number) => {
		const fare = hotelFares[fareIndex];
		updateRoomDraft(roomIndex, {
			roomFareIndex: fareIndex,
			cost: fare?.price ?? 0,
		});
	};

	const handleCostChange = (roomIndex: number, raw: string) => {
		const parsed = parseFloat(raw.replace(',', '.'));
		updateRoomDraft(roomIndex, { cost: Number.isFinite(parsed) ? parsed : 0 });
	};

	const buildRoomsInput = (): AccommodationRoomInput[] =>
		roomDrafts.map((draft, roomIndex) => ({
			roomFare: hotelFares[draft.roomFareIndex] ?? hotelFares[0],
			passengerIds: getPassengerIdsForRoomIndex(roomIndex, quotation, savedPassengers),
			cost: draft.cost,
		}));

	const handleSaveHotel = async () => {
		setLocatorTouched(true);

		if (hotelFares.length === 0) {
			setError('El hotel seleccionado no tiene tarifas de habitación');
			return;
		}
		if (!tariffProviderId?.trim() || tariffProviderId === tariffProviderNotAssignedId) {
			setError('Seleccioná el proveedor de tarifas (ej. Hotelbeds)');
			return;
		}

		if (splitByRoom) {
			const missing = roomDrafts.findIndex((d) => !isPnrFilled(d.locator));
			if (missing >= 0) {
				setError(`Completá el localizador de la habitación ${missing + 1}`);
				return;
			}
		} else if (!isPnrFilled(serviceLocator)) {
			setError('Completá el localizador de la reserva antes de guardar');
			return;
		}

		const invalidCost = roomDrafts.findIndex((d) => !d.cost || d.cost <= 0);
		if (invalidCost >= 0) {
			setError(`Indicá un costo válido para la habitación ${invalidCost + 1}`);
			return;
		}

		setLoading(true);
		setError(null);

		try {
			if (splitByRoom) {
				for (let i = 0; i < roomDrafts.length; i += 1) {
					const draft = roomDrafts[i];
					const roomsInput: AccommodationRoomInput[] = [
						{
							roomFare: hotelFares[draft.roomFareIndex] ?? hotelFares[0],
							passengerIds: getPassengerIdsForRoomIndex(i, quotation, savedPassengers),
							cost: draft.cost,
						},
					];
					const payload = buildAccommodationServiceDraft({
						destination: currentDest,
						hotel: selectedHotel,
						roomsInput,
						quotation,
						reference: draft.locator,
						tariffProviderId,
					});
					await saveServiceToOrder(saleOrderId, payload);
				}
			} else {
				const payload = buildAccommodationServiceDraft({
					destination: currentDest,
					hotel: selectedHotel,
					roomsInput: buildRoomsInput(),
					quotation,
					reference: serviceLocator,
					tariffProviderId,
				});
				await saveServiceToOrder(saleOrderId, payload);
			}

			if (destIndex >= destinations.length - 1) {
				onComplete();
				return;
			}

			setDestIndex(destIndex + 1);
			setHotelIndex(0);
		} catch (e) {
			setError(e instanceof Error ? e.message : 'Error al guardar alojamiento');
		} finally {
			setLoading(false);
		}
	};

	const handleSkipDestination = () => {
		if (destIndex >= destinations.length - 1) {
			onComplete();
			return;
		}
		setDestIndex(destIndex + 1);
		setHotelIndex(0);
	};

	return (
		<Box>
			<Typography variant="h6" fontWeight={700} gutterBottom>
				Alojamientos
			</Typography>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				Destino {destIndex + 1} de {destinations.length}: {currentDest.cityName} · {currentDest.nights} noches
			</Typography>

			<Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
				<Typography variant="subtitle2" fontWeight={600} gutterBottom>
					Hotel confirmado
				</Typography>
				<RadioGroup value={hotelIndex} onChange={(e) => setHotelIndex(Number(e.target.value))}>
					{hotels.map((hotel, index) => {
						const minPrice = hotel.rooms.length ? Math.min(...hotel.rooms.map((r) => r.price)) : undefined;
						return (
							<FormControlLabel
								key={`${hotel.hotelId}-${index}`}
								value={index}
								control={<Radio />}
								label={
									<Box>
										<Typography fontWeight={600}>{hotel.hotelName}</Typography>
										<Typography variant="caption" color="text.secondary">
											{hotel.provider}
											{hotel.rooms.length > 0
												? ` · ${hotel.rooms.length} tarifa${hotel.rooms.length > 1 ? 's' : ''}${
														minPrice !== undefined ? ` · desde $${minPrice}` : ''
												  }`
												: ''}
										</Typography>
									</Box>
								}
							/>
						);
					})}
				</RadioGroup>
			</Paper>

			{selectedHotel && (
				<Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
					<Typography variant="subtitle2" fontWeight={600} gutterBottom>
						Proveedor y prestador
					</Typography>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Destino en orden: <strong>{currentDest.cityName || currentDest.cityCode}</strong> (formato del catálogo).
					</Typography>
					<Box sx={{ mb: 2 }}>
						<WizardProviderAutocomplete
							value={tariffProviderId}
							onChange={setTariffProviderId}
							error={!tariffProviderId || tariffProviderId === tariffProviderNotAssignedId}
							helperText={
								selectedHotel.provider
									? `Cotización: integración ${selectedHotel.provider} — buscá ese proveedor de tarifas`
									: undefined
							}
						/>
					</Box>
					<Alert severity="info" icon={false} sx={{ bgcolor: 'action.hover' }}>
						<Typography variant="body2" fontWeight={600} gutterBottom>
							Prestador (hotel)
						</Typography>
						<Typography variant="body2">{selectedHotel.hotelName}</Typography>
						<Typography variant="caption" color="text.secondary">
							ID {selectedHotel.hotelId} · {mapHotelProviderToAccommodationType(selectedHotel.provider)}
						</Typography>
					</Alert>
				</Paper>
			)}

			{hotelFares.length > 0 && (
				<Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
					<Typography variant="subtitle2" fontWeight={600} gutterBottom>
						{hotelRoomsMatchRoomConfig(selectedHotel, roomConfig.length)
							? 'Tarifas cotizadas por habitación'
							: 'Tarifas disponibles en cotización'}
					</Typography>
					{hotelRoomsMatchRoomConfig(selectedHotel, roomConfig.length) ? (
						<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
							En la cotización hay una tarifa por habitación del viaje; cada fila abajo usa la suya por defecto.
						</Typography>
					) : null}
					{hotelFares.map((fare, index) => (
						<Typography key={index} variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
							·{' '}
							{hotelRoomsMatchRoomConfig(selectedHotel, roomConfig.length)
								? `Hab. ${index + 1}: ${getRoomFareLabel(fare)}`
								: getRoomFareLabel(fare)}
						</Typography>
					))}
				</Paper>
			)}

			<Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
				<Typography variant="subtitle2" fontWeight={600} gutterBottom>
					Habitaciones del viaje
				</Typography>
				<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
					Margen de la cotización: {markup}% — la venta se calcula igual que en la orden.
				</Typography>

				{hotelFares.length === 0 ? (
					<Alert severity="warning">Este hotel no tiene tarifas cargadas en la cotización.</Alert>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Habitación</TableCell>
								<TableCell>Tarifa</TableCell>
								<TableCell>Costo (USD)</TableCell>
								<TableCell align="right">Venta</TableCell>
								{splitByRoom && <TableCell>Localizador</TableCell>}
							</TableRow>
						</TableHead>
						<TableBody>
							{roomDrafts.map((draft, roomIndex) => {
								const conf = roomConfig[roomIndex];
								const sale = draft.cost > 0 ? parseFloat(calcularPrecioFinal(draft.cost, markup)) : 0;
								const locatorError = locatorTouched && splitByRoom && !isPnrFilled(draft.locator);

								return (
									<React.Fragment key={roomIndex}>
										<TableRow>
											<TableCell>
												<Typography fontWeight={600}>
													Hab. {roomIndex + 1}
													{multiRoom ? ` · ${formatRoomConfigCaption(conf)}` : ''}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{passengerLabelForRoom(roomIndex)}
												</Typography>
											</TableCell>
											<TableCell>
												<FormControl size="small" fullWidth sx={{ minWidth: 200 }}>
													<Select
														value={draft.roomFareIndex}
														onChange={(e) => handleFareChange(roomIndex, Number(e.target.value))}
													>
														{hotelFares.map((fare, fareIndex) => (
															<MenuItem key={fareIndex} value={fareIndex}>
																{hotelRoomsMatchRoomConfig(selectedHotel, roomConfig.length)
																	? `Opción ${fareIndex + 1}: ${getRoomFareLabel(fare)}`
																	: getRoomFareLabel(fare)}
															</MenuItem>
														))}
													</Select>
												</FormControl>
											</TableCell>
											<TableCell>
												<TextField
													size="small"
													type="number"
													inputProps={{ min: 0, step: 0.01 }}
													value={draft.cost}
													onChange={(e) => handleCostChange(roomIndex, e.target.value)}
													InputProps={{
														startAdornment: <InputAdornment position="start">$</InputAdornment>,
													}}
													sx={{ maxWidth: 120 }}
												/>
											</TableCell>
											<TableCell align="right" sx={{ fontWeight: 600, color: 'success.main' }}>
												${sale.toFixed(2)}
											</TableCell>
											{splitByRoom && (
												<TableCell>
													<TextField
														required
														size="small"
														placeholder="LOC-..."
														value={draft.locator}
														onChange={(e) =>
															updateRoomDraft(roomIndex, {
																locator: e.target.value.toUpperCase(),
															})
														}
														onBlur={() => setLocatorTouched(true)}
														error={locatorError}
														inputProps={{
															style: { textTransform: 'uppercase', fontWeight: 600 },
														}}
													/>
												</TableCell>
											)}
										</TableRow>
									</React.Fragment>
								);
							})}
							<TableRow>
								<TableCell colSpan={2}>
									<strong>Total{splitByRoom ? ' (suma por habitación)' : ''}</strong>
								</TableCell>
								<TableCell>
									<strong>${totals.totalCost.toFixed(2)}</strong>
								</TableCell>
								<TableCell align="right">
									<strong>${totals.totalSale.toFixed(2)}</strong>
								</TableCell>
								{splitByRoom && <TableCell />}
							</TableRow>
						</TableBody>
					</Table>
				)}
			</Paper>

			{multiRoom && (
				<Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
					<FormControlLabel
						control={
							<Switch
								checked={splitByRoom}
								onChange={(e) => {
									setSplitByRoom(e.target.checked);
									setLocatorTouched(false);
								}}
							/>
						}
						label={
							<Box>
								<Typography fontWeight={600}>Separar en servicios distintos</Typography>
								<Typography variant="caption" color="text.secondary">
									Un servicio de alojamiento por habitación, cada uno con su localizador y costo.
								</Typography>
							</Box>
						}
					/>
				</Paper>
			)}

			{!splitByRoom && (
				<Paper
					variant="outlined"
					sx={{
						p: 2.5,
						mb: 2,
						borderWidth: 2,
						borderColor: serviceLocatorError ? 'error.main' : 'primary.main',
						bgcolor: 'action.hover',
					}}
				>
					<Typography variant="subtitle1" fontWeight={700} gutterBottom color="primary.main">
						Reserva del alojamiento
					</Typography>
					<Alert severity="info" sx={{ mb: 2 }}>
						Ingresá el <strong>localizador</strong> de la reserva con el proveedor — es obligatorio para guardar.
					</Alert>
					<TextField
						required
						fullWidth
						size="medium"
						label="Localizador *"
						placeholder="Ej. HB-123456"
						value={serviceLocator}
						onChange={(e) => setServiceLocator(e.target.value.toUpperCase())}
						onBlur={() => setLocatorTouched(true)}
						error={serviceLocatorError}
						helperText={
							serviceLocatorError
								? 'El localizador es obligatorio (no uses "-")'
								: 'Código de confirmación del hotel / broker'
						}
						inputProps={{ style: { textTransform: 'uppercase', fontWeight: 600, letterSpacing: 1 } }}
					/>
				</Paper>
			)}

			{splitByRoom && (
				<Alert severity="info" sx={{ mb: 2 }}>
					Completá un localizador por habitación en la tabla. Se crearán {roomDrafts.length} servicios de alojamiento
					para este destino.
				</Alert>
			)}

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}

			<Box display="flex" justifyContent="space-between">
				<Button variant="text" onClick={handleSkipDestination} disabled={loading}>
					Omitir destino
				</Button>
				<Button variant="contained" color="success" onClick={() => void handleSaveHotel()} disabled={loading}>
					{loading ? (
						<CircularProgress size={22} color="inherit" />
					) : destIndex >= destinations.length - 1 ? (
						'Guardar y continuar'
					) : (
						'Guardar hotel y siguiente destino'
					)}
				</Button>
			</Box>
		</Box>
	);
};
